<template>
  <transition name="pwa-banner-slide">
    <div v-if="isVisible" class="pwa-install-banner" :class="{ behind: stackIndex > 0 }">
      <div class="banner-text">
        <h4 class="banner-title">{{ $t("DownloadAppPage.bannerTitle") }}</h4>
        <p class="banner-subtitle">{{ $t("DownloadAppPage.bannerSubTitle") }}</p>
      </div>
      <div class="banner-actions">
        <v-btn
          class="banner-install-btn"
          color="#bd9478"
          size="small"
          height="34"
          rounded="lg"
          :loading="loading"
          @click="install"
        >
          <span>{{ $t("DownloadAppPage.bannerInstall") }}</span>
        </v-btn>
        <v-btn
          class="banner-later-btn"
          variant="text"
          size="small"
          height="28"
          @click="dismiss"
        >
          <span>{{ $t("DownloadAppPage.bannerLater") }}</span>
        </v-btn>
      </div>

      <!-- Manual install instructions (iOS / browsers without a native prompt) -->
      <InstallGuideSheet v-model="bottomSheet" />
    </div>
  </transition>
</template>

<script>
import { mapGetters } from "vuex";
import InstallGuideSheet from "./InstallGuideSheet.vue";
import {
  registerBanner,
  unregisterBanner,
  bannerIndex,
} from "../../utils/bannerStack";

const BANNER_NAME = "pwa-install";
const DISMISS_KEY = "pwaInstallBannerDismissedAt";
// Re-show the banner this long after the user dismisses it (7 days).
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000;

export default {
  components: { InstallGuideSheet },
  data() {
    return {
      deferredPrompt: window.deferredPwaPrompt || null,
      bottomSheet: false,
      loading: false,
      dismissed: this.isRecentlyDismissed(),
      installed: false,
    };
  },
  computed: {
    ...mapGetters({
      isPwaInstalled: "isPwaInstalled",
    }),
    stackIndex() {
      return bannerIndex(BANNER_NAME);
    },
    isVisible() {
      return !this.isPwaInstalled && !this.installed && !this.dismissed;
    },
  },
  mounted() {
    window.addEventListener("pwa-installable", this.onInstallable);
    window.addEventListener("pwa-installed", this.onInstalled);
    if (this.isVisible) {
      registerBanner(BANNER_NAME, 2); // behind the push opt-in banner
    }
  },
  unmounted() {
    window.removeEventListener("pwa-installable", this.onInstallable);
    window.removeEventListener("pwa-installed", this.onInstalled);
    unregisterBanner(BANNER_NAME);
  },
  methods: {
    isRecentlyDismissed() {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY));
      if (!dismissedAt) return false;
      return Date.now() - dismissedAt < DISMISS_DURATION;
    },
    onInstallable() {
      this.deferredPrompt = window.deferredPwaPrompt;
    },
    onInstalled() {
      this.installed = true;
      unregisterBanner(BANNER_NAME);
    },
    async install() {
      // Native install prompt (Android Chrome, desktop Chrome/Edge)
      if (this.deferredPrompt) {
        this.loading = true;
        try {
          this.deferredPrompt.prompt();
          const { outcome } = await this.deferredPrompt.userChoice;
          if (outcome === "accepted") {
            this.installed = true;
          }
          this.deferredPrompt = null;
          window.deferredPwaPrompt = null;
        } catch (err) {
          console.log(err);
        } finally {
          this.loading = false;
        }
        return;
      }
      // Fallback: manual "Add to Home Screen" instructions (iOS, etc.)
      this.bottomSheet = true;
    },
    dismiss() {
      this.dismissed = true;
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
      unregisterBanner(BANNER_NAME);
    },
  },
};
</script>

<style scoped lang="scss">
.pwa-install-banner {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 11;
  display: flex;
  align-items: center;
  gap: 14px;
  width: calc(100% - 32px);
  max-width: 520px;
  padding: 14px 16px;
  background: #ffffffee;
  border: 1px solid #ececec;
  border-radius: 16px;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.15);
  transition: transform 0.35s ease, opacity 0.35s ease;

  // Card-deck look: the non-front banner peeks out behind the front one
  &.behind {
    transform: translateX(-50%) translateY(-14px) scale(0.96);
    z-index: 10;
    pointer-events: none;
  }

  .banner-text {
    flex: 1;
    min-width: 0;

    .banner-title {
      font-size: 18px;
      font-weight: 600;
      color: $blackColor;
      margin: 0;
    }

    .banner-subtitle {
      font-size: 14px;
      color: #7c7c7c;
      margin: 2px 0 0 0;
    }
  }

  .banner-actions {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
  }

  .banner-install-btn {
    color: #ffffff !important;
    font-size: 14px;
  }

  .banner-later-btn {
    color: #9e9e9e !important;
    font-size: 13px;
    min-width: unset;
  }
}

// Slide-up enter / leave transition
.pwa-banner-slide-enter-active,
.pwa-banner-slide-leave-active {
  transition: transform 0.35s ease, opacity 0.35s ease;
}
.pwa-banner-slide-enter-from,
.pwa-banner-slide-leave-to {
  transform: translate(-50%, 120%);
  opacity: 0;
}

@media (max-width: 601px) {
  .pwa-install-banner {
    gap: 10px;
    padding: 10px 12px;

    .banner-text .banner-title {
      font-size: 16px;
    }
    .banner-text .banner-subtitle {
      font-size: 12px;
    }
  }
}
</style>
